import { useSyncExternalStore } from "react";

// 질문 유형. 서버 SurveysUseCase(QUESTION_TYPES) · 공개 페이지 SurveyForm.jsx 와 같아야 한다
export const QUESTION_TYPES = [
    { value: 'SHORT_TEXT', label: '단답형', icon: '—' },
    { value: 'LONG_TEXT', label: '장문형', icon: '≡' },
    { value: 'SINGLE_CHOICE', label: '객관식', icon: '◉' },
    { value: 'MULTIPLE_CHOICE', label: '체크박스', icon: '☑' },
    { value: 'DROPDOWN', label: '드롭다운', icon: '▾' },
    { value: 'LINEAR_SCALE', label: '선형 배율', icon: '⋯' },
    { value: 'RATING', label: '별점', icon: '★' },
    { value: 'DATE', label: '날짜', icon: '▦' },
    { value: 'CHOICE_GRID', label: '객관식 그리드', icon: '⊞' },
    { value: 'CHECKBOX_GRID', label: '체크박스 그리드', icon: '⊠' },
    { value: 'SECTION', label: '제목·설명', icon: 'T' },
    { value: 'PAGE', label: '섹션 나누기', icon: '§' },
];
export const CHOICE_TYPES = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'DROPDOWN'];
export const GRID_TYPES = ['CHOICE_GRID', 'CHECKBOX_GRID'];
export const OTHER_TYPES = ['SINGLE_CHOICE', 'MULTIPLE_CHOICE']; // "기타" 직접 입력
export const BRANCH_TYPES = ['SINGLE_CHOICE', 'DROPDOWN']; // 답에 따라 섹션 이동
export const OTHER_ID = '_other';
export const SUBMIT = 'SUBMIT';
export const isAnswerable = (q) => q.type !== 'SECTION' && q.type !== 'PAGE';
export const typeLabel = (type) => QUESTION_TYPES.find(t => t.value === type)?.label;

export const MAX_TITLE_LENGTH = 100;
export const MAX_DESCRIPTION_LENGTH = 2000;
export const MAX_QUESTIONS = 100;
export const MAX_OPTIONS = 50;
export const MAX_GRID_ROWS = 30;
export const MAX_GRID_COLUMNS = 10;
export const MAX_RESPONSES = 10000;

export const STATUS_LABEL = {
    DRAFT: { label: '임시저장', className: 'bg-gray-100 text-gray-500' },
    OPEN: { label: '응답 받는 중', className: 'bg-emerald-50 text-emerald-700' },
    CLOSED: { label: '마감', className: 'bg-gray-900 text-white' },
};

/** 질문·선택지 ID (서버는 영문·숫자 1~20자만 받는다) */
export const newId = () => Math.random().toString(36).slice(2, 10);

export function newQuestion(type) {
    return {
        id: newId(),
        type,
        title: '',
        description: '',
        required: false,
        ...(CHOICE_TYPES.includes(type) || GRID_TYPES.includes(type) ? { options: [{ id: newId(), label: '' }] } : {}),
        ...(GRID_TYPES.includes(type) ? { rows: [{ id: newId(), label: '' }] } : {}),
        ...(type === 'LINEAR_SCALE' ? { scale: { min: 1, max: 5, minLabel: '', maxLabel: '' } } : {}),
        ...(type === 'RATING' ? { scale: { min: 1, max: 5 } } : {}),
    };
}

/** 유형을 바꿔도 제목·설명·선택지(그리드는 열)·행·기타·섹션 이동·형식 검사는 쓸 수 있는 만큼 이어서 쓴다 */
export function changeType(question, type) {
    const base = newQuestion(type);
    const maxOptions = GRID_TYPES.includes(type) ? MAX_GRID_COLUMNS : MAX_OPTIONS;
    return {
        ...base,
        id: question.id,
        title: question.title,
        description: question.description,
        required: isAnswerable({ type }) && question.required,
        ...(base.options && question.options ? { options: question.options.slice(0, maxOptions) } : {}),
        ...(base.rows && question.rows ? { rows: question.rows } : {}),
        ...(OTHER_TYPES.includes(type) && question.allowOther ? { allowOther: true } : {}),
        ...(BRANCH_TYPES.includes(type) && question.branching ? { branching: true } : {}),
        ...(question.validation && (type === 'SHORT_TEXT' || (type === 'LONG_TEXT' && question.validation.kind === 'LENGTH')) ? { validation: question.validation } : {}),
    };
}

/** 저장할 수 없는 이유 (없으면 null) */
export function questionError(question, index) {
    const name = `${index + 1}번째 ${typeLabel(question.type)}`;
    if (!question.title.trim()) {
        return `${name}의 제목을 입력해 주세요.`;
    }
    if (CHOICE_TYPES.includes(question.type) && !question.options.some(o => o.label.trim())) {
        return `"${question.title}" 선택지를 하나 이상 입력해 주세요.`;
    }
    if (GRID_TYPES.includes(question.type) && (!question.rows.some(o => o.label.trim()) || !question.options.some(o => o.label.trim()))) {
        return `"${question.title}" 행과 열을 하나 이상씩 입력해 주세요.`;
    }
    const { kind, min, max } = question.validation || {};
    if ((kind === 'NUMBER' || kind === 'LENGTH') && min != null && max != null && min > max) {
        return `"${question.title}" 응답 형식 검사의 최소값이 최대값보다 커요.`;
    }
    // 서버 답 길이 한도: 단답형 500자, 장문형 5000자
    const limit = question.type === 'SHORT_TEXT' ? 500 : 5000;
    if (kind === 'LENGTH' && (min > limit || max > limit)) {
        return `"${question.title}" 글자 수는 ${limit.toLocaleString()}자까지 정할 수 있어요.`;
    }
    return null;
}

/** 저장할 모양으로 (빈 선택지·행은 빼고, 섹션 이동을 끈 질문의 이동할 곳도 뺀다) */
export function toQuestionBody(question) {
    const clean = (list, keepGoTo) => list.filter(o => o.label.trim()).map(o => ({ id: o.id, label: o.label.trim(), ...(keepGoTo && o.goTo ? { goTo: o.goTo } : {}) }));
    return {
        ...question,
        title: question.title.trim(),
        description: question.description?.trim() || '',
        ...(question.options ? { options: clean(question.options, question.branching) } : {}),
        ...(question.rows ? { rows: clean(question.rows, false) } : {}),
    };
}

/**
 * 섹션 번호: PAGE 질문마다 { 질문id: 번호 }. 첫 질문이 PAGE 가 아니면 그 앞이 섹션 1.
 * 이동할 곳은 그 질문보다 뒤에 있는 섹션이나 제출만 된다 (서버도 같은 규칙, 맞지 않는 값은 버린다).
 */
export function sectionNumbers(questions) {
    let number = questions[0]?.type === 'PAGE' ? 0 : 1;
    return Object.fromEntries(questions.filter(q => q.type === 'PAGE').map(q => [q.id, ++number]));
}

/** index 번째 질문에서 갈 수 있는 섹션들 [{ id, label }] */
export function sectionTargets(questions, index, numbers) {
    return questions.slice(index + 1).filter(q => q.type === 'PAGE').map(q => ({ id: q.id, label: `섹션 ${numbers[q.id]}${q.title.trim() ? ` · ${q.title.trim()}` : ''}` }));
}

// 공유·페이지 링크용 정식 주소 (이야기 링크·페이지 주소 복사와 같이 shool.uk)
export const surveyUrl = (domainName, id) => `https://shool.uk/${domainName}/f/${id}`;

// 설문 미리보기: 편집기(FgSurveyEditor)가 쓰는 중인 설문을 두고, 미리보기(FdSurveyPreview)가 읽는다
let draft = null;
const listeners = new Set();

export function setSurveyPreviewDraft(next) {
    draft = next;
    listeners.forEach(listener => listener());
}

const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export function useSurveyPreviewDraft() {
    return useSyncExternalStore(subscribe, () => draft);
}

/** 미리보기에서 누른 질문의 편집 카드로: 스크롤 + 잠깐 테두리 + 제목 칸 포커스 */
export function focusSurveyQuestion(id) {
    const card = document.querySelector(`[data-survey-question="${id}"]`);
    if (!card) {
        return;
    }
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.setAttribute('data-flash', '');
    setTimeout(() => card.removeAttribute('data-flash'), 1200);
    card.querySelector('input[data-question-title]')?.focus({ preventScroll: true });
}
